import { motion } from 'framer-motion';
import { 
  LayoutDashboard, ArrowRight, CheckCircle, XCircle,
  Eye, FolderKanban, Users, MapPin, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const problems = [
  "Gerer avec des papiers et des notes",
  "Utiliser WhatsApp pour tout",
  "Se fier a sa memoire",
  "Oublier de relancer les clients",
];

const commercialStages = ['Prospect', 'Devis envoye', 'Devis accepte', 'A planifier', 'Projet gagne'];
const chantierStages = ['A planifier', 'Demarre', 'En cours', 'Termine'];

const commercialExamples = [
  { name: 'Maison Dupont', desc: 'Renovation salle de bain', stage: 'Devis envoye' },
  { name: 'Entreprise Martin', desc: 'Installation piscine', stage: 'Devis accepte' },
  { name: 'Villa Garcia', desc: 'Renovation terrasse', stage: 'A planifier' },
];

const chantierExamples = [
  { name: 'Piscine Durand', address: '12 rue des Lilas', team: 'Equipe A', stage: 'En cours' },
  { name: 'SDB Leroy', address: '45 av. Victor Hugo', team: 'Equipe B', stage: 'Demarre' },
  { name: 'Terrasse Rossi', address: '8 chemin du Lac', team: 'Equipe A', stage: 'A planifier' },
];

const benefits = [
  "Ne plus oublier un client",
  "Voir ses chantiers en un coup d'oeil",
  "Organiser son activite",
  "Piloter son entreprise",
];

export default function PipelinesPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Pipelines Visuels | Gestion Prospects & Chantiers - BatiAssist"
        description="Visualise tous tes projets en un coup d'œil avec les pipelines BatiAssist : suivi prospects, gestion devis, planification chantiers et organisation équipes."
        keywords="pipeline commercial artisan, suivi prospect BTP, gestion projet bâtiment, kanban chantier, tableau de bord artisan"
        path="/fonctionnalites/pipelines"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-ba-primary/10 text-ba-primary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <LayoutDashboard className="w-4 h-4" />
              Pipelines
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Pilotez ton entreprise{' '}
              <span className="text-ba-secondary">comme un tableau de bord</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Visualise immédiatement tes <strong>prospects</strong>, tes <strong>devis</strong> et tes <strong>chantiers</strong>. 
              Tout est organise, rien n'est oublie.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Organiser mon entreprise
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-3 py-1 bg-red-100 text-red-600 text-xs font-semibold uppercase rounded-full mb-4">
                Le probleme
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Beaucoup d'artisans travaillent encore sans organisation
              </h2>
              <p className="text-lg text-slate-600">
                Papiers, notes, WhatsApp, memoire... Le resultat : des devis oublies, 
                des clients non relances et des chantiers mal organises.
              </p>
            </motion.div>
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-red-50 rounded-xl border border-red-100"
                >
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <span className="text-slate-700">{problem}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pipeline Commercial */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-ba-primary/10 text-ba-primary text-xs font-semibold uppercase rounded-full mb-4">
              <FolderKanban className="w-4 h-4" />
              Pipeline Commercial
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Suivez tes prospects et tes devis
            </h2>
            <p className="text-lg text-slate-600">
              Chaque prospect avance dans le pipeline jusqu'a devenir un projet gagne.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg p-6 overflow-x-auto"
          >
            <div className="flex gap-4 min-w-[800px]">
              {commercialStages.map((stage, stageIndex) => (
                <div key={stageIndex} className="flex-1 min-w-[180px]">
                  <div className="bg-slate-100 rounded-lg px-4 py-2 mb-4">
                    <h4 className="font-semibold text-slate-700 text-sm">{stage}</h4>
                  </div>
                  <div className="space-y-3">
                    {commercialExamples.filter(e => e.stage === stage).map((example, i) => (
                      <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                        <p className="font-medium text-slate-800 mb-1">{example.name}</p>
                        <p className="text-sm text-slate-500">{example.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pipeline Chantier */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-ba-secondary/10 text-ba-secondary text-xs font-semibold uppercase rounded-full mb-4">
              <Calendar className="w-4 h-4" />
              Pipeline Chantier
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Organisez tes chantiers visuellement
            </h2>
            <p className="text-lg text-slate-600">
              Du chantier a planifier au chantier termine, tout est visible.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-50 rounded-2xl shadow-lg p-6 overflow-x-auto"
          >
            <div className="flex gap-4 min-w-[700px]">
              {chantierStages.map((stage, stageIndex) => (
                <div key={stageIndex} className="flex-1 min-w-[160px]">
                  <div className="bg-ba-secondary/10 rounded-lg px-4 py-2 mb-4">
                    <h4 className="font-semibold text-ba-secondary text-sm">{stage}</h4>
                  </div>
                  <div className="space-y-3">
                    {chantierExamples.filter(e => e.stage === stage).map((example, i) => (
                      <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                        <p className="font-medium text-slate-800 mb-2">{example.name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                          <MapPin className="w-3 h-3" />
                          {example.address}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Users className="w-3 h-3" />
                          {example.team}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Comment ca fonctionne ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'Nouveau prospect', desc: 'Un client te contacte, il apparait dans la colonne Prospect' },
              { step: 2, title: 'Devis envoye', desc: 'Tu envoies le devis, la carte passe dans Devis envoye' },
              { step: 3, title: 'Devis accepte', desc: 'Le client accepte, la carte avance automatiquement' },
              { step: 4, title: 'Chantier planifie', desc: 'Le projet passe dans le pipeline chantier' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-ba-primary rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8">
                Les benefices pour ton entreprise
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <CheckCircle className="w-6 h-6 text-ba-success flex-shrink-0" />
                    <span className="text-lg text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-ba-primary to-blue-700 rounded-3xl p-8 text-white"
            >
              <Eye className="w-12 h-12 mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-4">Vision claire de ton activité</h3>
              <p className="text-white/80 leading-relaxed">
                En un coup d'oeil, tu sais ou en sont tes prospects, tes devis et tes chantiers. 
                Plus besoin de chercher dans tes notes ou tes emails.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-ba-primary">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Organisez ton entreprise des maintenant
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Arretez de perdre des clients et des opportunites. Passez au pipeline visuel.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-bold rounded-full px-10 py-7 text-lg shadow-xl">
                Organiser mon entreprise
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
